<template>
  <div class="welcome-section ws-grid ws-grid--no-scroll">
    <div class="welcome-section__background" :style="backgroundStyles"></div>
    <div class="welcome-section__logo welcome-section__content logo">
      <universkin-logo class="logo__icon" :color="logoColor" />
      <span class="u-typography-helvetica u-text u-text--s">{{ $t('welcomePage.poweredBy') }}</span>
    </div>

    <div class="welcome-section__content welcome-section__main-content main-content">
      <div
        class="main-content__greeting u-typography-helvetica u-text u-text--l u-text--m-gte960 u-text--l-gte1440"
      >
        {{ greetingText }}
      </div>
      <div class="main-content__title" :class="titleClasses">
        {{ titleText }}
      </div>
      <div class="main-content__message" :class="messageTextClasses">
        {{ $t(messageText) }}
      </div>
      <div class="main-content__your-doctor" :class="yourDoctorClasses">
        {{ $t('common.signatureText') }}
      </div>
      <div class="main-content__signature">{{ doctorFullName }}</div>
      <u-button
        class="main-content__button"
        :dark="!isDesktopLayout"
        :disabled="isLoading"
        :size="actionButtonSize"
        @click="$emit('next')"
        >{{ $t('action.letsGo') }}</u-button
      >
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { UButton } from 'universkin-shared';

import UniverskinLogo from '@/components/welcome-section/UniverskinLogo.vue';

import { getPhotoTypePictureById } from '@/utils/common';

import { GENDER } from '@/constants/common';
import { types } from '@/store/types';

const RENEWAL_LANDING_TEXT_FIRST_ID = 5;

export default {
  name: 'WelcomeSection',
  components: { UniverskinLogo, UButton },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      backgroundImageUrl: null
    };
  },
  computed: {
    ...mapState({
      initialPageData: types.state.INITIAL_PAGE_DATA
    }),
    landingTextId() {
      return this.initialPageData.landingTextId;
    },
    isOfferRenewal() {
      return this.landingTextId >= RENEWAL_LANDING_TEXT_FIRST_ID;
    },
    patientName() {
      return this.initialPageData.patientName;
    },
    doctorFullName() {
      return this.initialPageData.doctorFullName;
    },
    profilePictureId() {
      return this.initialPageData.imageId;
    },
    patientGender() {
      return this.initialPageData.gender;
    },
    logoColor() {
      return this.isDesktopLayout ? 'black' : 'white';
    },
    backgroundStyles() {
      return {
        backgroundImage: this.backgroundImageUrl ? `url(${this.backgroundImageUrl})` : ''
      };
    },
    greetingText() {
      const appealing =
        this.patientGender === GENDER.MALE
          ? 'welcomePage.greeting.maleAppealing'
          : 'welcomePage.greeting.femaleAndOtherAppealing';

      return this.$t('welcomePage.greeting.greeting', [this.$t(appealing), this.patientName]);
    },
    titleText() {
      const text = this.isOfferRenewal
        ? 'welcomePage.greeting.renewalText'
        : 'welcomePage.greeting.text';

      return this.$t(text);
    },
    messageText() {
      return this.isOfferRenewal
        ? 'welcomePage.description.renewalPart1'
        : 'welcomePage.description.part1';
    },
    isDesktopLayout() {
      return this.$windowWidth.value >= 1329;
    },
    titleClasses() {
      const width = this.$windowWidth.value;

      if (width >= 1440) {
        return ['u-typography-helvetica u-text-display u-text-display--m'];
      }

      if (width >= 960) {
        return ['u-typography-helvetica u-text-display u-text-display--s'];
      }

      return ['u-typography-helvetica u-text-display u-text-display--m'];
    },
    messageTextClasses() {
      const width = this.$windowWidth.value;

      if (width >= 600 && width < 960) {
        return ['u-typography-helvetica', 'u-text', 'u-text--l'];
      }

      return ['u-typography-helvetica', 'u-text', 'u-text--m'];
    },
    actionButtonSize() {
      if (this.$windowWidth.value >= 600) {
        return 'large';
      }

      return 'medium';
    },
    yourDoctorClasses() {
      const width = this.$windowWidth.value;

      if (width >= 600 && width < 960) {
        return ['u-typography-helvetica', 'u-text', 'u-text--m'];
      }

      return ['u-typography-helvetica', 'u-text', 'u-text--s'];
    }
  },
  watch: {
    profilePictureId: 'setBackgroundPicture'
  },
  created() {
    this.setBackgroundPicture();
  },
  methods: {
    async setBackgroundPicture() {
      this.backgroundImageUrl = await getPhotoTypePictureById(this.profilePictureId);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/colors.scss';

.welcome-section {
  color: white;
  width: 100%;
  height: var(--screen-height);

  position: relative;
  z-index: 0;
  padding: 32px 16px 8px 16px;
  overflow-y: auto;

  &::before {
    content: '';
    display: block;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: #463e38;
    opacity: 0.5;
    z-index: -1;
  }

  &__background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--screen-height);
    background-position: right center;
    background-repeat: no-repeat;
    background-size: cover;
    z-index: -2;

    &:lang(ar) {
      transform: scaleX(-1); // mirror image horizontally
    }
  }

  &__logo {
    margin-bottom: 32px;
  }

  &__content {
    max-width: 464px;
    margin-inline-start: auto;
    margin-inline-end: auto;
  }
}

.logo {
  display: flex;
  align-items: center;

  &__icon {
    margin-inline-end: 16px;
  }
}

.main-content {
  &__greeting {
    margin-bottom: 16px;
  }

  &__title {
    margin-bottom: 24px;
  }

  &__message {
    margin-bottom: 24px;
  }

  &__your-doctor {
    margin-bottom: 8px;
  }

  &__signature {
    font-family: var(--u-font-family-Quentin);
    font-size: 30px;
    line-height: 4.4rem;
    margin-bottom: 24px;
  }

  &__button {
    width: 100%;
  }
}

@media (min-width: 375px) {
  .welcome-section {
    padding-top: 64px;

    &__logo {
      margin-bottom: 80px;
    }
  }
}

@media (min-width: 414px) {
  .welcome-section {
    &__logo {
      margin-bottom: 144px;
    }
  }
}

@media (min-width: 600px) {
  .welcome-section {
    padding-top: 164px;

    &__logo {
      margin-bottom: 64px;
    }
  }

  .main-content {
    &__title {
      margin-bottom: 48px;
    }

    &__signature {
      margin-bottom: 48px;
    }
  }
}

@media (min-width: 960px) {
  .welcome-section {
    padding: 8vh var(--ws-grid-m);

    &__content {
      max-width: unset;
      width: var(--ws-col-5);
      margin-inline-start: calc(var(--ws-col-2) + var(--ws-grid-g));
      margin-inline-end: 0;
    }

    &__logo {
      margin-bottom: 64px;
    }
  }

  .main-content {
    &__title {
      margin-bottom: 24px;
    }

    &__button {
      min-width: 260px;
      width: unset;
    }

    &__signature {
      font-size: 54px;
      margin-bottom: 24px;
    }
  }
}

@media (min-width: 1152px) {
  .main-content {
    &__title,
    &__signature {
      margin-bottom: 32px;
    }
  }
}

@media (min-width: 1329px) {
  .welcome-section {
    color: var(--u-color-black-6);

    &::before {
      display: none;
    }
  }

  .main-content {
    &__your-doctor {
      color: #5a5957;
    }

    &__greeting {
      color: $tuatara-color;
    }
  }
}

@media (min-width: 1920px) {
  .welcome-section {
    &__content {
      margin-inline-start: calc(var(--ws-col-1) + var(--ws-grid-g));
    }

    &__logo {
      margin-bottom: 150px;
    }
  }

  .main-content {
    &__title,
    &__signature {
      margin-bottom: 48px;
    }

    &__button {
      min-width: 295px;
    }
  }
}
</style>
